// @flow

import { API } from "aws-amplify";
import React, { useState, useEffect, useContext, useRef } from "react";
import { Container, Row } from "react-bootstrap";
import Form from "react-bootstrap/Form";
import Button from "react-bootstrap/Button";
import Card from "react-bootstrap/Card";
import InputGroup from "react-bootstrap/InputGroup";
import {
  queryShortcutBindingsByKeyBindingSessionIdIndex,
  queryUserMacrosByUserIdTriggerWordIndex,
} from "./../graphql/queries";
import {
  onUpdateShortcutBindingBySession,
  onCreateUserMacro,
  onUpdateUserMacro,
  onDeleteUserMacro,
} from "./../graphql/subscriptions";
import { queryUserKeyCommandsByUserIdCommandNameIndex as queryUserKeyCommands } from "./../graphql/queries";

import { useImeSafeEnter } from "./../utils/util";
import { publishMqtt } from "./../utils/MqttClient";
import { subscribeAppSync } from "./../utils/AppSyncSubscription";
import { useConnectionMonitor } from "./../utils/ConnectionStatus";
import {
  sendMessage as enqueueMessage,
  resendFailed,
  dismissFailed,
  useSendStatus,
} from "./../utils/MessageSender";
import { useUserPresence } from "./../utils/SessionUsers";
import UserContext from "./../utils/UserContext";
import { getSessionUsernameTopic } from "../utils/SessionUsers";
import { PUBLISH_LINE_BREAK } from "../utils/util";

type Props = $ReadOnly<{
  sessionId: string,
  shortcutInput: string,
  shortcutInputIndex: number,
  setSourceWord: React.Dispatch<React.SetStateAction<any>>,
  translatedWord: string,
}>;

const styles = {
  within: {
    marginRight: "10px",
    float: "right",
    width: "fit-content",
    color: "gray",
  },
  exceed: {
    marginRight: "10px",
    float: "right",
    width: "fit-content",
    color: "red",
  },
  sendRetrying: {
    marginTop: "6px",
    padding: "4px 8px",
    borderRadius: "4px",
    background: "#fff3cd",
    color: "#664d03",
    fontSize: "14px",
  },
  sendFailed: {
    marginTop: "6px",
    padding: "4px 8px",
    borderRadius: "4px",
    background: "#f8d7da",
    color: "#842029",
    fontSize: "14px",
  },
  sendFailedText: {
    marginRight: "8px",
  },
};

async function fetchBindings(sessionId) {
  try {
    const apiData = await API.graphql({
      query: queryShortcutBindingsByKeyBindingSessionIdIndex,
      variables: {
        keyBindingSessionId: sessionId,
      },
    });
    return (
      apiData.data?.queryShortcutBindingsByKeyBindingSessionIdIndex?.items ?? []
    );
  } catch (e) {
    console.log(e);
  }
}

async function fetchUserMacro(userId) {
  try {
    const apiData = await API.graphql({
      query: queryUserMacrosByUserIdTriggerWordIndex,
      variables: {
        userId: userId,
      },
    });
    return apiData.data.queryUserMacrosByUserIdTriggerWordIndex.items;
  } catch (e) {
    console.log(e);
  }
}

async function fetchUserKeyCommands(userId) {
  try {
    const apiData = await API.graphql({
      query: queryUserKeyCommands,
      variables: {
        userId: userId,
      },
    });
    return apiData.data.queryUserKeyCommandsByUserIdCommandNameIndex.items;
  } catch (e) {
    console.log(e);
  }
}

export default function ChatInputView(props: Props): React$Element<any> {
  const [textInput, setTextInput] = useState("");
  const [bindings, setBindings] = useState([]);
  const userContext = useContext(UserContext);
  const sendStatus = useSendStatus();
  // 확인 요청을 계속 보내 연결 끊김을 잡고 Direct Send Lambda도 데워 둔다 (#204)
  useConnectionMonitor();

  const ref = useRef(null);
  const isMounted = useRef(false);

  const [focusInputKey, setFocusInputKey] = useState("F1");
  const [clearSubtitlesKey, setClearSubtitlesKey] = useState("F8");

  // Macro Function
  const [macroArray, setMacroArray] = useState([]);
  const [cursorPosition, setCursorPosition] = useState(0);
  const [typingWord, setTypingWord] = useState("");

  // Max text length function
  const [maxCount, setMaxCount] = useState(100);

  // Initial macro fetch
  useEffect(() => {
    handleFetchMacro();
  }, []);

  const handleFetchMacro = () => {
    async function updateFetch() {
      const macro = await fetchUserMacro(userContext.userId);
      // 조회가 실패하면(undefined) 기존 목록을 유지한다 — 덮어쓰면 sort/find에서 화면 전체가 죽는다
      if (Array.isArray(macro)) {
        setMacroArray(macro);
      }
    }
    updateFetch();
  };

  // User Key Command initial fetch
  useEffect(() => {
    async function initialFetch() {
      const fetchedKeyCommands = await fetchUserKeyCommands(userContext.userId);
      if (!Array.isArray(fetchedKeyCommands)) return;
      const hasValue = (command) => {
        for (const obj of fetchedKeyCommands) {
          if (obj.commandName === command) {
            return true;
          }
        }
        return false;
      };
      hasValue("focusInput") &&
        setFocusInputKey(
          fetchedKeyCommands.find(
            (command) => command.commandName === "focusInput"
          ).triggerKey
        );
      hasValue("clearSubtitles") &&
        setClearSubtitlesKey(
          fetchedKeyCommands.find(
            (command) => command.commandName === "clearSubtitles"
          ).triggerKey
        );
    }
    initialFetch();
  }, [props.sessionId]);

  // Subscribe to macro mutations (create/update/delete) - 끊기면 다시 구독 (#201)
  useEffect(() => {
    if (!userContext.userId) return;
    const subscriptions = [
      onCreateUserMacro,
      onUpdateUserMacro,
      onDeleteUserMacro,
    ].map((query) =>
      subscribeAppSync(
        { query, variables: { userId: userContext.userId } },
        {
          context: "ChatInputView macro",
          next: () => {
            handleFetchMacro();
          },
          // 끊긴 사이 바뀐 매크로를 다시 불러온다
          onResubscribed: handleFetchMacro,
        }
      )
    );
    return () => subscriptions.forEach((s) => s.unsubscribe());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userContext.userId]);

  // Sort macro array
  useEffect(() => {
    macroArray.sort((a, b) => (a.macroName > b.macroName ? 1 : -1));
  }, [macroArray]);

  // AutoTranslate disabled - setSourceWord intentionally not called

  /////////////////////////////////////////////////////////////////////////////////////////////////////////////

  // Inits the sessionUsers object and publishes the user connection status
  useUserPresence(props.sessionId, userContext.username);

  useEffect(() => {
    async function initialFetch() {
      const fetchedBindings = await fetchBindings(props.sessionId);
      if (Array.isArray(fetchedBindings)) {
        setBindings(fetchedBindings);
      }
    }
    initialFetch();
  }, [props.sessionId]);

  // Shortcut input by button click, inefficient because this re-renders sheet
  useEffect(() => {
    if (isMounted.current) {
      insertShortcut(props.shortcutInput);
    } else {
      isMounted.current = true;
    }
  }, [props.shortcutInputIndex]);

  // 전송은 항상 이 함수를 거친다. 한글 IME 조합 중에는 React state(textInput)가
  // 실제 입력창 값보다 늦을 수 있어, 값은 DOM에서 읽어 넘겨받는다.
  function sendMessage(text) {
    //  Don't create a message if the message is empty
    if (text === "") {
      return;
    }
    enqueueMessage({
      sessionId: props.sessionId,
      text,
      username: userContext.username,
      context: "ChatInputView.sendMessage",
    });
    setTextInput("");
    if (ref.current) {
      ref.current.value = "";
    }
    // 입력창을 코드로 비우면 onChange가 발생하지 않으므로, partner 화면이
    // (waiting...)으로 돌아가도록 여기서 직접 발행한다.
    publishTextChange(PUBLISH_LINE_BREAK);
  }

  const enterToSend = useImeSafeEnter(sendMessage, ref);

  function handleSendMessage(event) {
    event.preventDefault();
    enterToSend.submitNow();
  }

  // Subscribe to update shortcut by session - 끊기면 다시 구독 (#201)
  useEffect(() => {
    if (!props.sessionId) return;
    const sessionId = props.sessionId;
    const refetch = async () => {
      const fetchedBindings = await fetchBindings(sessionId);
      // 조회가 실패하면(undefined) 기존 단축키를 유지한다
      if (Array.isArray(fetchedBindings)) {
        setBindings(fetchedBindings);
      }
    };
    const subscription = subscribeAppSync(
      {
        query: onUpdateShortcutBindingBySession,
        variables: { keyBindingSessionId: sessionId },
      },
      {
        context: "ChatInputView shortcut",
        next: () => {
          refetch();
        },
        onResubscribed: refetch,
      }
    );
    return () => subscription.unsubscribe();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.sessionId]);

  useEffect(() => {
    window.addEventListener("keydown", handleShortcutPress);
    return () => {
      window.removeEventListener("keydown", handleShortcutPress);
    };
  }, [bindings]);

  useEffect(() => {
    window.addEventListener("keydown", handleFocusInput);
    return () => {
      window.removeEventListener("keydown", handleFocusInput);
    };
  }, [focusInputKey]);

  useEffect(() => {
    window.addEventListener("keydown", handleDeleteMessages);
    return () => {
      window.removeEventListener("keydown", handleDeleteMessages);
    };
  }, [clearSubtitlesKey]);

  useEffect(() => {
    window.addEventListener("keydown", handleAutoTranslate);
    return () => {
      window.removeEventListener("keydown", handleAutoTranslate);
    };
  }, [props.translatedWord]);

  // Use useEffect to add the click event listener
  useEffect(() => {
    if (ref.current) {
      ref.current.addEventListener("click", handleMouseClick);
    }
    return () => {
      if (ref.current) {
        ref.current.removeEventListener("click", handleMouseClick);
      }
    };
  }, []);

  const handleMouseClick = () => {
    if (!ref.current) return;
    const textarea = ref.current;
    setCursorPosition(textarea.selectionStart);
  };

  // HandleShortcutPress V 2.0
  const handleShortcutPress = (event) => {
    bindings.map((binding) => {
      if (event.ctrlKey || event.metaKey) {
        if (event.key === binding.key) {
          console.log("shortcut triggered");
          event.preventDefault();
          insertShortcut(binding.text);
        }
      }
    });
  };

  // Function to insert a shortcut at the cursor position
  const insertShortcut = (shortcut) => {
    if (!ref.current) return;

    const textarea = ref.current;
    const selectionStart = textarea.selectionStart;
    const selectionEnd = textarea.selectionEnd;
    const newCursorPosition = selectionStart + shortcut.length;

    // Insert the shortcut at the cursor position
    const newText =
      textarea.value.substring(0, selectionStart) +
      shortcut +
      textarea.value.substring(selectionEnd);

    // setTextInput(newText);
    textarea.value = newText;

    // Set the cursor position to the end of the inserted shortcut
    textarea.selectionStart = newCursorPosition;
    textarea.selectionEnd = newCursorPosition;
    textarea.focus();
    setTextInput(newText);
  };

  // // Handle Macro v 1.0
  // const handleMacro = (event) => {
  //   const inputValue = event.target.value;
  //   const matchingMacro = macroArray.find(
  //     (macro) => inputValue.endsWith(macro.triggerWord) && macro.isEnabled
  //   );
  //   const insert = matchingMacro?.insertWord || inputValue;
  //   setTextInput(
  //     inputValue.substring(
  //       0,
  //       inputValue.length - matchingMacro?.triggerWord?.length || 0
  //     ) + insert
  //   );
  // };

  const findWhiteSpace = (string, startIndex) => {
    if (startIndex <= 0) {
      // Handle edge case where startIndex is 0 or negative
      return -1;
    }

    // Iterate in reverse from startIndex to 0
    for (let i = startIndex - 1; i >= 0; i--) {
      if (/\s/.test(string[i])) {
        // Found a whitespace character before the given index
        return i;
      }
    }

    // No whitespace found before the given index, return -1
    return -1;
  };

  // Handle Macro v 2.0
  const handleMacro = (event) => {
    if (!ref.current) return;

    const inputValue = event.target.value;
    const textarea = ref.current;
    const selectionStart = textarea.selectionStart;
    const selectionEnd = textarea.selectionEnd;
    const whiteSpaceIndex = findWhiteSpace(inputValue, selectionStart);
    const detectedTrigger = inputValue.slice(
      whiteSpaceIndex + 1,
      selectionStart
    );

    const matchingMacro = macroArray.find(
      (macro) => detectedTrigger === macro.triggerWord && macro.isEnabled
    );

    const insert = matchingMacro?.insertWord || inputValue;

    if (matchingMacro) {
      const before = inputValue.substring(
        0,
        selectionStart - matchingMacro?.triggerWord?.length
      );
      const after = inputValue.substring(selectionStart, inputValue.length);

      const newText = before + insert + after;

      const newCursorPosition =
        selectionStart +
        -matchingMacro?.triggerWord?.length +
        matchingMacro?.insertWord?.length;

      textarea.value = newText;

      textarea.selectionStart = newCursorPosition;
      textarea.selectionEnd = newCursorPosition;

      textarea.focus();

      setTextInput(newText);
    } else {
      setTextInput(insert);
    }
  };

  // const handleTypingWord = (event) => {
  //   console.log(event.target.value);
  //   setTypingWord(event.target.value);
  // };

  // useEffect(() => {
  //   window.addEventListener("keyup", handleTypingWord);
  //   return () => {
  //     window.removeEventListener("keyup", handleTypingWord);
  //   };
  // }, []);

  const handleFocusInput = (event) => {
    if (event.key === focusInputKey) {
      event.preventDefault();
      ref.current.focus();
    }
  };

  const handleDeleteMessages = (event) => {
    if (event.key === clearSubtitlesKey) {
      event.preventDefault();
      enqueueMessage({
        sessionId: props.sessionId,
        text: " ",
        context: "ChatInputView.clearSubtitles",
      });
    }
  };

  const handleAutoTranslate = (event) => {
    if (event.key === "Tab") {
      // event.preventDefault();
      console.log(props.translatedWord);

      // function replaceLastWord(str, replacement) {
      //   var words = str.split(' ');
      //   words[words.length - 1] = replacement; // Replace the last word with the desired replacement
      //   return words.join(' '); // Join the words back into a string
      // }

      // setTextInput(replaceLastWord(textInput, props.translatedWord));
    }
  };

  async function publishTextChange(text) {
    if (userContext.username === "") {
      return;
    }
    const topic = getSessionUsernameTopic(
      props.sessionId,
      userContext.username
    );
    // 실패하면 죽은 연결을 걷어내고 한 번 더 보낸다. 그래도 실패하면 로그만 남긴다 (#145)
    await publishMqtt(topic, text, "ChatInputView.publishTextChange");
  }

  // onChange 전용 핸들러. 입력이 확정·취소되는 시점의 발행은 sendMessage와
  // Escape 처리에서 직접 한다 (onChange 이벤트에는 event.key가 없다).
  function handleTextsInputChange(event) {
    if (userContext.username === "") {
      return;
    }
    publishTextChange(event.target.value);
  }

  return (
    <Card>
      <Card.Header as="h5">
        Input{" "}
        <span style={{ fontSize: "15px", fontWeight: "normal" }}>
          ({focusInputKey})
        </span>
      </Card.Header>
      <Card.Body className="m-0 pb-2">
        <Container fluid className="p-0 m-0">
          <Row>
            <InputGroup className="mb-2">
              <Form.Control
                id="chat-input-field"
                type="text"
                placeholder="Enter message"
                ref={ref}
                onChange={(event) => {
                  handleMacro(event);
                  handleTextsInputChange(event);
                }}
                value={textInput}
                autoFocus="autofocus"
                // onBlur={focusOnBlur}
                onCompositionStart={enterToSend.handleCompositionStart}
                onCompositionEnd={enterToSend.handleCompositionEnd}
                // onKeyUp={(event) => { // Unused because of race issue
                onKeyDown={(event) => {
                  // 조합 중 Enter는 여기서 전송하지 않는다 (useImeSafeEnter 주석 참고)
                  if (enterToSend.handleKeyDown(event)) {
                    return;
                  }
                  if (event.key === "Escape") {
                    enterToSend.cancel();
                    setTextInput("");
                    publishTextChange(PUBLISH_LINE_BREAK);
                  }
                }}
              />
              <Button
                variant="primary"
                onClick={handleSendMessage}
                disabled={textInput === ""}
              >
                Send
              </Button>
            </InputGroup>
            <div>
              <span
                style={
                  textInput.length <= maxCount ? styles.within : styles.exceed
                }
              >{`${textInput.length}/${maxCount}`}</span>
            </div>
            {/* 전송이 막히거나 실패한 줄을 번역자가 바로 알 수 있게 표시한다 (#181) */}
            {sendStatus.retrying && (
              <div style={styles.sendRetrying}>
                Retrying send… ({sendStatus.pendingCount} queued)
              </div>
            )}
            {sendStatus.failed.map((item) => (
              <div key={item.key} style={styles.sendFailed}>
                <span style={styles.sendFailedText}>
                  Failed to send: "{item.text}"
                </span>
                <Button
                  size="sm"
                  variant="danger"
                  onClick={() => resendFailed(item.key)}
                >
                  Resend
                </Button>{" "}
                <Button
                  size="sm"
                  variant="outline-secondary"
                  onClick={() => dismissFailed(item.key)}
                >
                  Dismiss
                </Button>
              </div>
            ))}
            {/* Typing : {typingWord} */}
            {/* {props.translatedWord} */}
            {/* <Button
              onClick={() => {
                insertShortcut("Yo");
              }}
            >
              Insert Shortcut
            </Button> */}
          </Row>
        </Container>
      </Card.Body>
    </Card>
  );
}
